import { useState, useEffect } from 'react';
import { BASE_API_URL } from '../../Enums';
import { useSCE } from '../../Components/context/SceContext';
import { getAllCardsFromDb, deleteCardFromDb, editCardAlias } from '../../APIFunctions/CardReader';
import ConfirmationModal from '../../Components/DecisionModal/ConfirmationModal';
import { trashcanSymbol, pencilSymbol, checkSymbol, cancelSymbol } from '../Overview/SVG';

const header = [
  'Time'.padEnd(29),
  'Endpoint'.padEnd(20),
  'Method'.padEnd(8),
  'Code'.padEnd(7),
  'Alias'.padEnd(18),
  'Event'.padEnd(21)
].join('');

export default function CardReader() {
  const { user } = useSCE();
  const token = user.token;

  const [logs, setLogs] = useState([]);
  const [cards, setCards] = useState([]);
  const [toggleDelete, setToggleDelete] = useState(false);
  const [cardToDelete, setCardToDelete] = useState({});
  const [editingCardId, setEditingCardId] = useState(null);
  const [editedAlias, setEditedAlias] = useState('');
  const [tab, setTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('tab') || 'registry';
  });
  const [connected, setConnected] = useState(true);
  const [connectionStatusText, setConnectionStatusText] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [paginationText, setPaginationText] = useState('');
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const getSelectedClassName = (currTab) => {
    let className = 'p-2 hover:bg-gray-400 rounded-xl ';
    if (currTab === tab) {
      className += 'text-blue-500 underline underline-offset-4';
    } else {
      className += 'dark:text-white text-gray-700';
    }
    return className;
  };

  const getColumnClassName = (columnName) => {
    let className = 'px-6 py-3 whitespace-nowrap ';
    if (['lastVerifiedAt', 'registrationDate'].includes(columnName)) {
      className += 'hidden md:table-cell ';
    } else if (columnName === 'verifiedCount') {
      className += 'hidden lg:table-cell';
    }
    return className;
  };

  function buildLog(data) {
    const time = new Date().toISOString().padEnd(29);
    const endpoint = data.endpoint.padEnd(20);
    const method = data.requestType.padEnd(8);
    const code = String(data.statusCode).padEnd(7);
    const event = data.message.padEnd(21);
    const alias = (data.alias || 'no alias')?.padEnd(18);
    return [time, endpoint, method, code, alias, event].join('');
  }

  async function getAllCards() {
    setLoading(true);
    const apiResponse = await getAllCardsFromDb({
      token,
      page,
    });
    if (!apiResponse.error) {
      setCards(apiResponse.responseData.items);
      setTotal(apiResponse.responseData.total);
      setRowsPerPage(apiResponse.responseData.rowsPerPage);
    }
    setLoading(false);
  }

  function handleTabChange(newTab) {
    setTab(newTab);
    const params = new URLSearchParams(window.location.search);
    params.set('tab', newTab);
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState({}, '', newUrl);
  }

  function handleDeleteClick(card) {
    setToggleDelete(!toggleDelete);
    setCardToDelete(card);
  }

  function handleEditClick(card) {
    if (editingCardId === card._id) {
      setEditingCardId(null);
      setEditedAlias('');
    } else {
      setEditingCardId(card._id);
      setEditedAlias(card.alias);
    }
  }

  async function handleSaveEdit(cardId) {
    if (!editedAlias.trim()) {
      return; // Don't save empty alias
    }

    try {
      const response = await editCardAlias(token, cardId, editedAlias.trim());
      if (!response.error) {
        // Refetch all cards from database to ensure UI matches server reality
        await getAllCards();
        setEditingCardId(null);
        setEditedAlias('');
      }
    } catch (error) {
      setLogs(
        (currLogs) => [
          '[error] unable to update card alias, check browser logs: \n' + error,
          ...currLogs,
        ]
      );
    }
  }

  function handleEditKeyDown(key) {
    if (key === 'Enter') {
      handleSaveEdit(editingCardId);
    } else if (key === 'Escape') {
      setEditingCardId(null);
      setEditedAlias('');
    }
  }

  function renderInputOrAlias(card) {
    if (editingCardId === card._id) {
      return (
        <input
          type='text'
          value={editedAlias}
          onChange={(e) => setEditedAlias(e.target.value)}
          onKeyDown={(e) => handleEditKeyDown(e.key)}
          className='bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-white font-medium m-0 px-1 py-0 focus:outline-none focus:ring-1 focus:ring-blue-500'
          style={{ width: '16ch' }}
          autoFocus
        />
      );
    }
    return (
      <div style={{ width: '16ch', display: 'flex', justifyContent: 'center', overflow: 'hidden' }}>
        <span style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
          {card.alias}
        </span>
      </div>
    );
  }

  function CardEntry({ card }) {
    const isEditing = editingCardId === card._id;
    return (
      <tr key={card._id} className='bg-white border-b dark:bg-gray-800 dark:border-gray-700'>
        <td key='alias' className=''>
          <div className='px-6 py-4 font-medium text-gray-700 whitespace-nowrap dark:text-white'>
            {renderInputOrAlias(card)}
          </div>
        </td>
        <td key='createdAt' className='hidden md:table-cell'>
          <div className='px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white'>
            {card.createdAt}
          </div>
        </td>
        <td key='lastVerified' className='hidden md:table-cell'>
          <div className='px-6 py-4 font-medium text-gray-700 whitespace-nowrap dark:text-white'>
            {card.lastVerified}
          </div>
        </td>
        <td key='verifiedCount' className='hidden lg:table-cell'>
          <div className='px-6 py-4 font-medium text-gray-700 whitespace-nowrap dark:text-white'>
            {card.verifiedCount}
          </div>
        </td>
        <td>
          <div className='flex space-x-2 w-32'>
            <button
              className={`p-2 hover:bg-gray-200 dark:hover:bg-white/30 rounded-xl ${!isEditing ? 'invisible' : ''}`}
              onClick={() => handleSaveEdit(card._id)}
              title='Save changes'
            >
              {checkSymbol('#22c55e')}
            </button>
            <button
              className='p-2 hover:bg-gray-200 dark:hover:bg-white/30 rounded-xl'
              onClick={() => handleEditClick(card)}
              title={isEditing ? 'Cancel edit' : 'Edit alias'}
            >
              {isEditing ? (
                cancelSymbol('#ef4444')
              ) : (
                pencilSymbol('#6b7280')
              )}
            </button>
            <button
              className='p-2 hover:bg-gray-200 dark:hover:bg-white/30 rounded-xl'
              onClick={() => handleDeleteClick(card)}
              title='Delete card'
            >
              {trashcanSymbol('#e64539')}
            </button>
          </div>
        </td>
      </tr>
    );
  }


  useEffect(() => {
    getAllCards();
    const url = new URL('/api/OfficeAccessCard/listen', BASE_API_URL);
    url.searchParams.append('token', token);
    const eventSource = new EventSource(url.href);
    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        const url = new URL(data.endpoint, window.location.origin);
        // if card is verified or added successfully, query the DB again to get latest card data
        if (url.pathname === '/verify' && data.statusCode === 200) {
          getAllCards();
        }
        const newLog = buildLog(data);
        setLogs(currLogs => [newLog, ...currLogs]);
      } catch (err) { // if the message sent from error cannot be parsed
        setLogs(
          (currLogs) => [
            '[error] unable to format response, check browser logs: \n' + err,
            ...currLogs,
          ]
        );
      }
    };

    eventSource.onerror = () => { // if the connection to the event source encounters error
      setLogs(
        (currLogs) => [
          'EventSource didn\'t work, check browser logs',
          ...currLogs
        ]
      );
      setConnected(false);
    };

    return () => {
      eventSource.close();
    };
  }, []);

  useEffect(() => {
    if (connected) {
      setConnectionStatusText('Listening for card reader activity...');
      return;
    }
    setConnectionStatusText('Client disconnected. Please reload to reconnect.');
  }, [connected]);

  useEffect(() => {
    const amountOfCardsOnCurrentPage = Math.min((page + 1) * rowsPerPage, cards.length);
    const pageOffset = page * rowsPerPage;
    const startingElementNumber = (page * rowsPerPage) + 1;
    const endingElementNumber = amountOfCardsOnCurrentPage + pageOffset;
    setPaginationText(
      <>
        <p className='md:hidden text-gray-700 dark:text-white'>
          {startingElementNumber} - {endingElementNumber} / {total}
        </p>
        <p className="hidden md:inline-block text-gray-700 dark:text-white">
          Showing <span className='font-medium'>{startingElementNumber}</span> to <span className='font-medium'>{endingElementNumber}</span> of <span className='font-medium'>{total}</span> results
        </p>
      </>
    );
  }, [page, rowsPerPage, cards, total]);

  useEffect(() => {
    getAllCards();
  }, [page]);

  function maybeRenderPagination() {
    const amountofCardsOnCurrentPage = Math.min((page + 1) * rowsPerPage, cards.length);
    const pageOffset = page * rowsPerPage;
    const endingElementNumber = amountofCardsOnCurrentPage + pageOffset;
    if (cards.length) {
      return (
        <nav className='flex justify-start py-6 mx-4'>
          <div className='flex items-center navbar-start'>
            <span className="text-gray-700 dark:text-white">
              {loading ? '...' : paginationText}
            </span>
          </div>
          <div className='flex justify-end space-x-3 navbar-end'>
            <button
              className='btn btn-neutral text-gray-800 bg-gray-200 hover:bg-gray-300 dark:text-white dark:bg-gray-700 dark:hover:bg-gray-600'
              onClick={() => setPage(page - 1)}
              disabled={page === 0 || loading}
            >
              previous
            </button>
            <button
              className='btn btn-neutral text-gray-800 bg-gray-200 hover:bg-gray-300 dark:text-white dark:bg-gray-700 dark:hover:bg-gray-600'
              onClick={() => setPage(page + 1)}
              disabled={endingElementNumber >= total || loading}
            >
              next
            </button>
          </div>
        </nav>
      );
    }
    return <></>;
  }

  function maybeRenderTable() {
    if (cards.length === 0) {
      return (
        <h3 className='text-center text-lg pt-4 text-gray-700 dark:text-white text-base'>
          Looks like there are no registered cards...
        </h3>
      );
    }
    return (
      <div className='overflow-x-auto overflow-y-auto w-full'>
        <table className='m-full min-w-full text-gray-700 dark:text-gray-400'>
          <thead className='text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400'>
            <tr className=''>
              {[
                { title: 'Alias', columnName: 'alias' },
                { title: 'Registration Date', columnName: 'registrationDate' },
                { title: 'Last Verified At', columnName: 'lastVerifiedAt' },
                { title: 'Verified Count', columnName: 'verifiedCount' },
                { title:'', columnName: '' }
              ].map(({ title, columnName }) => (
                <th key={title}
                  className={getColumnClassName(columnName)}
                >
                  <div className='flex items-center justify-center'>
                    {title}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className='text-center text-sm'>
            {cards.map((card) => {
              return <CardEntry key={card._id} card={card} />;
            })}
          </tbody>
        </table>
      </div>
    );
  }

  function getComponentFromTabChoice(tab) {
    if (tab === 'registry') {
      return (
        <div>
          <ConfirmationModal {... {
            headerText: `Delete card: ${cardToDelete.alias}?`,
            bodyText: `Are you sure you want to delete 
              card: ${cardToDelete.alias}? It'll be gone forever if you do.`,
            confirmText: `Yes, delete ${cardToDelete.alias}`,
            cancelText: 'No, keep the card',
            confirmClassAddons: 'bg-red-600 hover:bg-red-500',
            handleConfirmation: async () => {
              await deleteCardFromDb(token, cardToDelete._id);
              await getAllCards();
              setToggleDelete(!toggleDelete);
            },
            handleCancel: () => setToggleDelete(!toggleDelete),
            open: toggleDelete
          }
          } />
          <div className='flex flex-col m-6'>
            {maybeRenderTable()}
            {maybeRenderPagination()}
          </div>
        </div>
      );
    }
    return (
      <div>
        <h3 className='text-center text-lg py-2 pt-4 text-gray-700 dark:text-white text-base'>
          {connectionStatusText}
        </h3>
        <pre className='overflow-x-auto m-4 text-gray-700 dark:text-white'>
          {header}
          {logs.map((log, index) => (
            <div key={index} className='border-b border-gray-300 py-1'>{log}</div>
          ))}
        </pre>
      </div>
    );
  }

  return (
    <div className='overview-container bg-gray min-h-[100dvh]'>
      <h1 className='text-center text-gray-700 dark:text-white text-4xl font-bold py-4'>SCE Card Reader Page</h1>
      <pre className='whitespace-normal text-center max-w-[90%] mx-auto text-gray-700 dark:text-white font-normal py-2'>This webpage manages RFID cards used to unlock the office door in the SCE room</pre>
      <div className='flex flex-row items-center justify-center text-gray-700 dark:text-white text-xl font-bold pt-4'>
        <button
          className={getSelectedClassName('registry')}
          onClick={() => handleTabChange('registry')}
        >
          Card Registry
        </button>
        {/* spacer to differentiate between the two options */}
        <div>&nbsp;|&nbsp;</div>
        <button
          className={getSelectedClassName('logs')}
          onClick={() => handleTabChange('logs')}
        >
          Card Reader Logs
        </button>
      </div>
      {getComponentFromTabChoice(tab)}
    </div>
  );
}
